/* BrokerageDetailPage — standalone full page for CRM deep-link at /brokerages/:id.
 *
 * When a user clicks the CRM link from a phone contact card, this page loads
 * showing full brokerage detail (info, notes, interaction log, realtors).
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrokerageDetail from "@/components/BrokerageDetail";

export default function BrokerageDetailPage() {
  const { brokerageId } = useParams();
  const navigate = useNavigate();
  const [brokerage, setBrokerage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!brokerageId) return;
    setLoading(true);
    setError(null);
    api
      .get(`/brokerages/${brokerageId}`)
      .then(({ data }) => setBrokerage(data))
      .catch((e) => {
        setError(formatApiError(e));
        toast.error("Failed to load brokerage");
      })
      .finally(() => setLoading(false));
  }, [brokerageId]);

  const handleUpdated = (updated) => {
    setBrokerage(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading brokerage…
      </div>
    );
  }

  if (error || !brokerage) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-slate-900">Brokerage not found</h2>
        <p className="text-sm text-slate-500 mt-1">{error || "This brokerage may have been deleted."}</p>
        <Button
          variant="outline"
          className="mt-4 min-h-[44px]"
          onClick={() => navigate("/brokerages")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Brokerages
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4" data-testid="brokerage-detail-page">
      {/* Back nav */}
      <div>
        <Link
          to="/brokerages"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Brokerages
        </Link>
      </div>

      {/* Shared detail component */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
        <BrokerageDetail brokerage={brokerage} onUpdated={handleUpdated} />
      </div>
    </div>
  );
}
